/*
  Buy Box V2 (Beta)

  Isolated controller for the new subscription / one-time buy box.
  It does NOT submit the cart itself: it keeps the hidden `id`, `selling_plan`
  and `quantity` inputs inside the section's native `{% form 'product' %}`
  (rendered by this same snippet, using the theme's <product-form> custom
  element) in sync with the selected tab/card/variant, then lets
  product-form.js handle the actual /cart/add.js submission, error states
  and cart refresh - exactly like the theme's own buy-buttons.liquid does.

  One-time ("Einmalkauf") pricing intentionally mirrors custom-buybox.js's
  Storefront API pricing-probe so displayed totals stay correct if a live
  Shopify Discount/Function changes the effective quantity-break price after
  this page was rendered. This file is a self-contained duplicate of that
  logic (not a shared import) to keep the two buy boxes fully isolated.

  Variant changes (a customer picking a different color/size on the same
  product) are handled the same way product-info.js already updates the
  rest of the page: by subscribing to the theme's PUB_SUB_EVENTS.variantChange
  pub/sub event and swapping in the freshly server-rendered `.buybox-v2`
  block for the new variant (correct plans/prices/sold-out state included),
  then re-initializing. `subscribe`/`publish`/`PUB_SUB_EVENTS` are globals
  provided by pubsub.js/constants.js, loaded earlier in <head>.
*/
(function () {
  'use strict';

  const STOREFRONT_API_VERSION = '2025-01';
  const PROBE_TTL_MS = 10 * 60 * 1000;
  const probeMemoryCache = new Map();
  const inFlightProbeRequests = new Map();
  let storefrontAccessTokenCache;

  function probeCacheKey(variantId, qty) {
    return 'buyboxV2PricingProbe:' + variantId + ':' + qty;
  }

  function shouldBypassProbeCache() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('preview_theme_id') || params.has('refresh_pricing')) return true;
    } catch (e) {}
    return false;
  }

  function getCachedProbe(variantId, qty) {
    if (shouldBypassProbeCache()) return null;
    const key = probeCacheKey(variantId, qty);
    if (probeMemoryCache.has(key)) return probeMemoryCache.get(key);
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.timestamp || Date.now() - parsed.timestamp > PROBE_TTL_MS) {
        window.localStorage.removeItem(key);
        return null;
      }
      probeMemoryCache.set(key, parsed);
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function setCachedProbe(variantId, qty, data) {
    const key = probeCacheKey(variantId, qty);
    const entry = Object.assign({}, data, { timestamp: Date.now() });
    probeMemoryCache.set(key, entry);
    if (shouldBypassProbeCache()) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {}
  }

  function getStorefrontAccessToken() {
    if (typeof storefrontAccessTokenCache === 'string' && storefrontAccessTokenCache) {
      return storefrontAccessTokenCache;
    }
    try {
      const featuresScript = document.getElementById('shopify-features');
      if (featuresScript && featuresScript.textContent) {
        const features = JSON.parse(featuresScript.textContent);
        if (features && typeof features.accessToken === 'string' && features.accessToken) {
          storefrontAccessTokenCache = features.accessToken;
          return storefrontAccessTokenCache;
        }
      }
    } catch (e) {}
    try {
      const tokenMeta = document.querySelector('meta[name="shopify-checkout-api-token"]');
      const token = tokenMeta && tokenMeta.getAttribute('content');
      if (typeof token === 'string' && token) {
        storefrontAccessTokenCache = token;
        return storefrontAccessTokenCache;
      }
    } catch (e) {}
    storefrontAccessTokenCache = null;
    return null;
  }

  function amountToCents(amount) {
    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed)) return 0;
    return Math.round(parsed * 100);
  }

  function formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /*
    Mirrors the integer math in snippets/buybox-v2-unit-price.liquid so the
    probe-refreshed one-time price and the server-rendered unit price never
    disagree. Only used to refresh a card's already-rendered unit-price text
    after an async pricing probe changes its total - never for first paint.
  */
  function computeUnitPriceText(totalCents, qty, gramsPerUnit, displayUnit) {
    const gramsTotal = qty * gramsPerUnit;
    if (!(gramsTotal > 0) || !(totalCents > 0)) return '';

    let scale;
    let decimals;
    let unitLabel;
    if (displayUnit === 'per_gram') {
      scale = 100;
      decimals = 10000;
      unitLabel = 'g';
    } else if (displayUnit === 'per_kg') {
      scale = 1000;
      decimals = 100;
      unitLabel = 'kg';
    } else {
      scale = 100;
      decimals = 100;
      unitLabel = '100 g';
    }

    const scaled = Math.round((totalCents * scale) / gramsTotal);
    const value = scaled / decimals;
    const decimalPlaces = Math.round(Math.log10(decimals));
    return '€' + value.toFixed(decimalPlaces).replace('.', ',') + ' / ' + unitLabel;
  }

  async function probeTierPricing(variantId, qty) {
    const storefrontAccessToken = getStorefrontAccessToken();
    if (!storefrontAccessToken) {
      throw new Error('missing storefront access token');
    }

    const response = await fetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({
        query: `
          mutation BuyboxV2Probe($merchandiseId: ID!, $quantity: Int!) {
            cartCreate(input: { lines: [{ merchandiseId: $merchandiseId, quantity: $quantity }] }) {
              cart {
                lines(first: 1) {
                  edges {
                    node {
                      quantity
                      cost {
                        totalAmount { amount currencyCode }
                        subtotalAmount { amount currencyCode }
                      }
                    }
                  }
                }
              }
              userErrors { field message }
            }
          }
        `,
        variables: {
          merchandiseId: 'gid://shopify/ProductVariant/' + variantId,
          quantity: qty,
        },
      }),
    });
    if (!response.ok) throw new Error('storefront probe failed (' + response.status + ')');

    const payload = await response.json();
    if (Array.isArray(payload.errors) && payload.errors.length) {
      throw new Error(payload.errors.map((error) => error.message).join('; '));
    }

    const userErrors =
      payload.data && payload.data.cartCreate && Array.isArray(payload.data.cartCreate.userErrors)
        ? payload.data.cartCreate.userErrors
        : [];
    if (userErrors.length) {
      throw new Error(userErrors.map((error) => error.message).join('; '));
    }

    const probeLine =
      payload.data && payload.data.cartCreate && payload.data.cartCreate.cart && payload.data.cartCreate.cart.lines
        ? payload.data.cartCreate.cart.lines.edges[0] && payload.data.cartCreate.cart.lines.edges[0].node
        : null;
    if (!probeLine || !probeLine.cost) {
      throw new Error('storefront probe returned no line cost');
    }

    const original = amountToCents(probeLine.cost.subtotalAmount && probeLine.cost.subtotalAmount.amount);
    const final = amountToCents(probeLine.cost.totalAmount && probeLine.cost.totalAmount.amount);

    return { originalLinePrice: original, finalLinePrice: final };
  }

  function ensureTierPricing(variantId, qty, options = {}) {
    const forceRefresh = options.forceRefresh === true;
    const key = probeCacheKey(variantId, qty);

    if (!forceRefresh) {
      const cached = getCachedProbe(variantId, qty);
      if (cached) return Promise.resolve(cached);
    }
    if (inFlightProbeRequests.has(key)) return inFlightProbeRequests.get(key);

    const request = probeTierPricing(variantId, qty)
      .then((result) => {
        setCachedProbe(variantId, qty, result);
        return result;
      })
      .catch((err) => {
        console.warn('[buybox-v2 pricing-probe] probe failed', err);
        return null;
      })
      .finally(() => {
        inFlightProbeRequests.delete(key);
      });

    inFlightProbeRequests.set(key, request);
    return request;
  }

  const TAB_ACTIVATION_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

  function initBuybox(root) {
    if (!root || !root.isConnected) return;

    const sectionId = root.dataset.sectionId;
    const variantId = root.dataset.variantId;
    const daysPerJar = parseInt(root.dataset.daysPerJar || '30', 10);
    const showUnitPriceFlag = root.dataset.showUnitPrice === 'true';
    const netGramsPerUnit = parseFloat(root.dataset.netGramsPerUnit || '0');
    const unitPriceDisplayUnit = root.dataset.unitPriceDisplayUnit || 'per_100g';
    const compareAtPrice = parseInt(root.dataset.compareAtPrice || '0', 10) || 0;

    const form = root.querySelector('form');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const submitPriceEl = root.querySelector('[data-buybox-v2-submit-price]');
    const submitComparePriceEl = root.querySelector('[data-buybox-v2-submit-compare-price]');
    const submitUnitPriceEl = root.querySelector('[data-buybox-v2-submit-unit-price]');

    const tabs = Array.from(root.querySelectorAll('.buybox-v2__tab'));
    const panels = {};
    root.querySelectorAll('[data-buybox-v2-panel]').forEach((panel) => {
      panels[panel.dataset.buyboxV2Panel] = panel;
    });

    let activeTab = panels[root.dataset.defaultTab] ? root.dataset.defaultTab : Object.keys(panels)[0];
    let onetimeProbesStarted = false;

    function cardsFor(type) {
      return panels[type] ? Array.from(panels[type].querySelectorAll('.buybox-v2__card')) : [];
    }

    function selectedCard(type) {
      if (!panels[type]) return null;
      const input = panels[type].querySelector('input[type="radio"]:checked');
      return input ? input.closest('.buybox-v2__card') : null;
    }

    function syncCardSelectedClass(type) {
      cardsFor(type).forEach((card) => {
        const input = card.querySelector('input[type="radio"]');
        card.classList.toggle('is-selected', !!(input && input.checked));
      });
    }

    // Recomputes the single unit-price line under the submit button from the
    // selected card's own quantity/total, the same way custom-buybox.js's
    // updatePricing() recalculates its one button-area unit price on every
    // state change instead of reading it back out of a per-option element.
    function updateSubmitUnitPrice(card) {
      if (!submitUnitPriceEl) return;
      const unitPriceSpan = submitUnitPriceEl.querySelector('.buybox-v2__unit-price');
      if (!unitPriceSpan || !card || !showUnitPriceFlag || !(netGramsPerUnit > 0)) return;

      const qty = parseInt(card.dataset.quantity || '1', 10);
      const totalCents = parseInt(card.dataset.totalCents || '0', 10);
      const unitPriceText = computeUnitPriceText(totalCents, qty, netGramsPerUnit, unitPriceDisplayUnit);
      if (unitPriceText) unitPriceSpan.textContent = unitPriceText;
    }

    // Mirrors custom-buybox.js's updatePricing(): comparePrice * quantity,
    // hidden whenever it isn't actually higher than the current total.
    function updateSubmitComparePrice(card) {
      if (!submitComparePriceEl || !card || !(compareAtPrice > 0)) return;

      const qty = parseInt(card.dataset.quantity || '1', 10);
      const totalCents = parseInt(card.dataset.totalCents || '0', 10);
      const compareTotal = compareAtPrice * qty;

      if (compareTotal > totalCents) {
        submitComparePriceEl.textContent = formatMoney(compareTotal);
        submitComparePriceEl.classList.remove('hidden');
      } else {
        submitComparePriceEl.classList.add('hidden');
      }
    }

    function updateFormForActiveTab() {
      const card = selectedCard(activeTab);
      if (!card) return;

      idInput.value = variantId;
      quantityInput.value = card.dataset.quantity || '1';

      if (activeTab === 'subscription') {
        sellingPlanInput.disabled = false;
        sellingPlanInput.value = card.dataset.sellingPlanId || '';
      } else {
        sellingPlanInput.disabled = true;
        sellingPlanInput.value = '';
      }

      const priceText = card.querySelector('[data-buybox-v2-card-total-text]');
      if (submitPriceEl && priceText) {
        submitPriceEl.textContent = priceText.textContent.trim();
      }

      updateSubmitUnitPrice(card);
      updateSubmitComparePrice(card);
    }

    function activateTab(type) {
      if (!panels[type]) return;
      activeTab = type;
      tabs.forEach((tab) => {
        const isActive = tab.dataset.tabType === type;
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      Object.keys(panels).forEach((key) => {
        panels[key].hidden = key !== type;
      });
      updateFormForActiveTab();

      if (type === 'onetime') {
        startOnetimeProbes();
      }
    }

    function onCardChange(event) {
      const card = event.target.closest('.buybox-v2__card');
      const type = card.closest('[data-buybox-v2-panel]').dataset.buyboxV2Panel;
      syncCardSelectedClass(type);
      if (type === activeTab) updateFormForActiveTab();
    }

    function applyProbeResultToCard(card, probe) {
      if (!probe || typeof probe.finalLinePrice !== 'number' || probe.finalLinePrice <= 0) return;

      const totalEl = card.querySelector('[data-buybox-v2-card-total-text]');
      const perDayEl = card.querySelector('[data-buybox-v2-card-per-day-text]');
      const compareTotalEl = card.querySelector('[data-buybox-v2-card-compare-total-text]');
      const qty = parseInt(card.dataset.quantity || '1', 10);

      if (totalEl) totalEl.textContent = formatMoney(probe.finalLinePrice);
      if (perDayEl && qty * daysPerJar > 0) {
        perDayEl.textContent = formatMoney(Math.round(probe.finalLinePrice / (qty * daysPerJar)));
      }
      // originalLinePrice/finalLinePrice come straight from the cart probe's
      // subtotal (pre-discount) vs total (post-discount) - the same live
      // Shopify Discounts signal custom-buybox.js's own probe reveals its
      // `.volume-compare-price` from, rather than anything computed locally.
      if (compareTotalEl) {
        if (typeof probe.originalLinePrice === 'number' && probe.originalLinePrice > probe.finalLinePrice) {
          compareTotalEl.textContent = formatMoney(probe.originalLinePrice);
          compareTotalEl.classList.remove('hidden');
        } else {
          compareTotalEl.classList.add('hidden');
        }
      }

      // Mirrors snippets/buybox-v2-onetime-card.liquid's own tier_discount
      // formula (round((base - total) * 100 / base)), but fed from the same
      // probe pair the compare-price above already uses, so a discount that
      // only exists as a live Shopify Discount - invisible to the Liquid-side
      // override/quantity_price_breaks math at render time - still surfaces
      // the "Save X%" badge once the probe confirms it, instead of leaving it
      // permanently hidden just because the initial render saw no discount.
      const savingsBadgeEl = card.querySelector('[data-buybox-v2-card-savings-badge]');
      const savingsTextEl = card.querySelector('[data-buybox-v2-card-savings-text]');
      if (savingsBadgeEl && savingsTextEl) {
        let discountPercent = 0;
        if (typeof probe.originalLinePrice === 'number' && probe.originalLinePrice > probe.finalLinePrice) {
          discountPercent = Math.round(((probe.originalLinePrice - probe.finalLinePrice) * 100) / probe.originalLinePrice);
        }
        if (discountPercent > 0) {
          savingsTextEl.textContent = discountPercent + '%';
          savingsBadgeEl.classList.remove('hidden');
        } else {
          savingsBadgeEl.classList.add('hidden');
        }
      }

      card.dataset.totalCents = String(probe.finalLinePrice);

      if (card.classList.contains('is-selected') && activeTab === 'onetime') {
        updateFormForActiveTab();
      }
    }

    function startOnetimeProbes() {
      if (onetimeProbesStarted) return;
      onetimeProbesStarted = true;

      cardsFor('onetime').forEach((card) => {
        const qty = parseInt(card.dataset.quantity || '0', 10);
        if (!qty) return;

        const cached = getCachedProbe(variantId, qty);
        if (cached) applyProbeResultToCard(card, cached);

        ensureTierPricing(variantId, qty, { forceRefresh: true }).then((result) => {
          if (result) applyProbeResultToCard(card, result);
        });
      });
    }

    function focusTab(tab) {
      tab.focus();
    }

    function onTabsKeydown(event) {
      if (!TAB_ACTIVATION_KEYS.includes(event.key)) return;
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let newIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        newIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        newIndex = 0;
      } else if (event.key === 'End') {
        newIndex = tabs.length - 1;
      }
      if (newIndex === null) return;

      event.preventDefault();
      const newTab = tabs[newIndex];
      activateTab(newTab.dataset.tabType);
      focusTab(newTab);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab.dataset.tabType));
      tab.addEventListener('keydown', onTabsKeydown);
    });

    root.querySelectorAll('.buybox-v2__card input[type="radio"]').forEach((input) => {
      input.addEventListener('change', onCardChange);
    });

    Object.keys(panels).forEach((type) => syncCardSelectedClass(type));
    activateTab(activeTab);

    /*
      Variant changes (customer picks a different option value on the same
      product) are broadcast by product-info.js as PUB_SUB_EVENTS.variantChange,
      carrying the parsed Document for the freshly re-rendered section. Swap in
      the fresh `.buybox-v2` block for the new variant (correct plans, prices
      and sold-out state) and reinitialize, mirroring how the rest of the page
      already patches itself from that same event/payload shape.
    */
    let unsubscribeVariantChange = function () {};

    function handleVariantChange(event) {
      const data = event && event.data;
      if (!data || !sectionId || String(data.sectionId) !== String(sectionId)) return;

      if (!data.variant) {
        const submitButton = root.querySelector('.buybox-v2__submit');
        const submitButtonText = submitButton && submitButton.querySelector('span');
        if (submitButton) submitButton.setAttribute('disabled', 'disabled');
        if (submitButtonText && window.variantStrings) {
          submitButtonText.textContent = window.variantStrings.unavailable;
        }
        return;
      }

      if (!data.html || typeof data.html.getElementById !== 'function' || !root.id) return;
      const fresh = data.html.getElementById(root.id);
      if (!fresh) return;

      unsubscribeVariantChange();
      root.replaceWith(fresh);
      initBuybox(fresh);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && sectionId) {
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }
  }

  function init() {
    document.querySelectorAll('.buybox-v2[data-block-id]').forEach(initBuybox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
